import { gql, useQuery } from '@apollo/client';
import {
  Box,
  Columns,
  Container,
  Heading,
  IconButton,
  Inline,
  Stack,
  Text,
  Visible,
} from '@spaceship-fspl/components';
import { DatedBalance, SaverPortfolio } from '@spaceship-fspl/graphql';
import {
  WebCompaniesInPortfolioPanel,
  WebCompaniesInPortfolioPanelVariables,
} from '@spaceship-fspl/graphql/src/__generated__/WebCompaniesInPortfolioPanel';
import {
  WebUseInstrumentMarketPrices,
  WebUseInstrumentMarketPricesVariables,
} from '@spaceship-fspl/graphql/src/__generated__/WebUseInstrumentMarketPrices';
import { TimeScale } from '@spaceship-fspl/helpers';
import {
  FeatherActivityIcon,
  FeatherChevronDownIcon,
  FeatherChevronLeftIcon,
  FeatherChevronRightIcon,
  FeatherChevronUpIcon,
  FeatherInfoIcon,
} from '@spaceship-fspl/icons-web';
import {
  backgroundColor,
  getSpace,
  match,
  padding,
  paddingX,
  paddingY,
  scrollbar,
  useBetweenMedia,
  zIndex,
} from '@spaceship-fspl/styles';
import {
  TrackingAction,
  TrackingCategory,
  withTrackingCategory,
} from '@spaceship-fspl/tracking';
import { Card, CardVariant, TabSwitcher } from 'components';
import React from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import styled, { css } from 'styled-components';

import { CompanyGraph } from './company-graph';
import { CompanyProfile } from './company-profile';
import { CompanyStatistics } from './company-statistics';
import { List, ListRef } from './list';

const SCROLLBAR_OFFSET = getSpace('sm');
const CARD_BOX_HEIGHT_MD = 320;
const CARD_BOX_HEIGHT_LG = 520;
const TAB_SWITCHER_HEIGHT = 32;

const CARD_VARIANT: {
  [key in SaverPortfolio]: CardVariant;
} = {
  [SaverPortfolio.EARTH]: 'blueGradient',
  [SaverPortfolio.UNIVERSE]: 'indigoGradient',
  [SaverPortfolio.INDEX]: 'indigoGradient',
  [SaverPortfolio.GALAXY]: 'indigo100Gradient',
  [SaverPortfolio.EXPLORER]: 'indigo100Gradient',
};

export interface CompaniesInPortfolioPanelProps {
  portfolio: SaverPortfolio;
  title: string;
  disclaimer: string;
  children?: React.ReactNode;
}

enum CompanyTabs {
  PERFORMANCE,
  ABOUT_COMPANY,
}

export const CompaniesInPortfolioPanel: React.FC<
  React.PropsWithChildren<CompaniesInPortfolioPanelProps>
> = withTrackingCategory('Companies in Portfolio')(
  React.memo(function CompaniesInPortfolioPanelInner({
    portfolio,
    title,
    disclaimer,
  }) {
    const listRef = React.useRef<ListRef>(null);
    const [selectedIndex, setSelectedIndex] = React.useState<
      number | undefined
    >(undefined);
    const [activeTab, setActiveTab] = React.useState<CompanyTabs>(
      CompanyTabs.PERFORMANCE,
    );
    const [timeScale, setTimeScale] = React.useState<TimeScale>(
      TimeScale.SIX_MONTHS,
    );
    const [isMobileListVisible, setMobileListVisible] =
      React.useState<boolean>(false);

    const resp = useQuery<
      WebCompaniesInPortfolioPanel,
      WebCompaniesInPortfolioPanelVariables
    >(
      gql`
        query WebCompaniesInPortfolioPanel($portfolio: SaverPortfolio!) {
          instruments(portfolios: [$portfolio]) {
            id
            name
            audLatestPrice
            audMarketCapitalisation
            ytdPercentageChange
            ytdPercentageHigh
            ytdPercentageLow
            targetAllocationPercentage(portfolio: $portfolio)
            profile(portfolio: $portfolio) {
              id
              bodyText
            }
          }
        }
      `,
      {
        skip: !portfolio,
        variables: {
          portfolio,
        },
      },
    );

    const instruments = React.useMemo(
      () =>
        resp.data?.instruments?.slice().sort((a, b) => {
          if (!a.name || !b.name) {
            return 0;
          }
          return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
        }) ?? [],
      [resp.data?.instruments],
    );

    // select the first company when the list of companies changes
    React.useEffect(
      () => setSelectedIndex(instruments.length > 0 ? 0 : undefined),
      [instruments],
    );

    const getNextIndex = React.useCallback(
      (index: number | undefined): number | undefined =>
        index !== undefined
          ? Math.min(index + 1, instruments.length - 1)
          : undefined,
      [instruments],
    );

    const getPrevIndex = React.useCallback(
      (index: number | undefined): number | undefined =>
        index !== undefined ? Math.max(index - 1, 0) : undefined,
      [],
    );

    const select = React.useCallback(
      (id: string | undefined) => {
        if (id === undefined) {
          setSelectedIndex(undefined);
          return;
        }
        const index = instruments.findIndex((company) => id === company.id);
        setSelectedIndex(index !== -1 ? index : undefined);
      },
      [instruments],
    );

    const selectNext = React.useCallback(
      () => setSelectedIndex((index) => getNextIndex(index)),
      [getNextIndex],
    );
    const selectPrev = React.useCallback(
      () => setSelectedIndex((index) => getPrevIndex(index)),
      [getPrevIndex],
    );

    const getNext = React.useCallback(() => {
      const nextIndex = getNextIndex(selectedIndex);
      if (!nextIndex) {
        return undefined;
      }
      return instruments[nextIndex];
    }, [instruments, selectedIndex, getNextIndex]);

    const getPrev = React.useCallback(() => {
      const prevIndex = getPrevIndex(selectedIndex);
      if (!prevIndex) {
        return undefined;
      }
      return instruments[prevIndex];
    }, [instruments, selectedIndex, getPrevIndex]);

    const handleMobileListToggle = (): void => {
      setMobileListVisible((isMobileListVisible) => !isMobileListVisible);
    };

    const handleListNextPage = (): void => {
      listRef.current?.scrollToNextPage();
    };

    const handleListPrevPage = (): void => {
      listRef.current?.scrollToPrevPage();
    };

    const selected =
      selectedIndex !== undefined ? instruments[selectedIndex] : undefined;
    const canSelectNext =
      selectedIndex !== undefined
        ? selectedIndex < instruments.length - 1
        : false;
    const canSelectPrev =
      selectedIndex !== undefined ? selectedIndex > 0 : false;

    const isBetweenSmAndMd = useBetweenMedia('sm', 'md');
    const instrumentMarketPrices = useInstrumentMarketPrices(selected?.id);

    return (
      <Box as="section" paddingY={{ xs: 'xxl', lg: 'xxxl' }}>
        <Container>
          <Stack spaceY={{ xs: 'md', md: 'lg', lg: 'xl' }}>
            <Columns spaceX={{ md: 'sm', lg: 'md' }}>
              <Columns.Column width={{ md: 2 / 8, lg: 2 / 12 }}>
                <TrackingCategory category="Companies">
                  <Box position="relative" height="100%">
                    <RemoveScroll
                      enabled={isBetweenSmAndMd && isMobileListVisible}
                    >
                      <StyledCompanyListModal isModal={isMobileListVisible}>
                        <Visible isHidden={{ md: true }} displayValue="block">
                          <Columns alignY="center">
                            <Columns.Column width="max">
                              <Text variant={2} color="neutral.070">
                                {title}
                              </Text>
                              <Heading variant={4}>{selected?.name}</Heading>
                            </Columns.Column>
                            <Columns.Column width="min">
                              <IconButton
                                icon={
                                  isMobileListVisible
                                    ? FeatherChevronUpIcon
                                    : FeatherChevronDownIcon
                                }
                                size="lg"
                                variant="dark"
                                onClick={handleMobileListToggle}
                                tracking={{
                                  action: isMobileListVisible
                                    ? TrackingAction.Hide
                                    : TrackingAction.Show,
                                }}
                                aria-label="Toggle company list view"
                              >
                                Down
                              </IconButton>
                            </Columns.Column>
                          </Columns>
                        </Visible>
                        <Box
                          display={{
                            xs: isMobileListVisible ? 'block' : 'none',
                            md: 'block',
                          }}
                          overflow="hidden"
                        >
                          <List
                            ref={listRef}
                            options={instruments.map((company) => ({
                              label: company.name ?? '',
                              value: company.id ?? '',
                            }))}
                            selected={selected?.id || undefined}
                            onSelect={(e) => {
                              setMobileListVisible(false);
                              select(e);
                            }}
                          />
                        </Box>
                        <Visible
                          isHidden={{ xs: true, md: false }}
                          displayValue="block"
                        >
                          <Inline spaceX="xxs">
                            <IconButton
                              icon={FeatherChevronDownIcon}
                              size="lg"
                              variant="dark"
                              onClick={handleListNextPage}
                              aria-label="Next page"
                            >
                              Down
                            </IconButton>
                            <IconButton
                              icon={FeatherChevronUpIcon}
                              size="lg"
                              variant="dark"
                              onClick={handleListPrevPage}
                              aria-label="Previous page"
                            >
                              Up
                            </IconButton>
                          </Inline>
                        </Visible>
                      </StyledCompanyListModal>
                    </RemoveScroll>
                  </Box>
                </TrackingCategory>
              </Columns.Column>

              <Columns.Column width={{ md: 6 / 8, lg: 10 / 12 }}>
                <TrackingCategory category="Company Details">
                  <Stack spaceY="lg">
                    <Visible
                      isHidden={{ xs: true, md: false }}
                      displayValue="block"
                    >
                      <Box display="flex">
                        <Box flexGrow={1}>
                          <Heading
                            variant={{ lg: 1, md: 3 }}
                            color="neutral.070"
                          >
                            {title}
                          </Heading>
                          <Heading variant={{ lg: 1, md: 3 }}>
                            {selected?.name}
                          </Heading>
                        </Box>
                        <Box flexShrink={0}>
                          <Inline spaceX="xxs">
                            <IconButton
                              icon={FeatherChevronLeftIcon}
                              size="lg"
                              variant="dark"
                              onClick={selectPrev}
                              isDisabled={!canSelectPrev}
                              tracking={{
                                action: TrackingAction.Select,
                                label: getPrev()?.name ?? '',
                              }}
                              aria-label="Previous portfolio company"
                            >
                              Down
                            </IconButton>
                            <IconButton
                              icon={FeatherChevronRightIcon}
                              size="lg"
                              variant="dark"
                              onClick={selectNext}
                              isDisabled={!canSelectNext}
                              tracking={{
                                action: TrackingAction.Select,
                                label: getNext()?.name ?? '',
                              }}
                              aria-label="Next portfolio company"
                            >
                              Up
                            </IconButton>
                          </Inline>
                        </Box>
                      </Box>
                    </Visible>
                    <Stack spaceY={{ xs: 'sm', md: 'md' }}>
                      <CompanyStatistics
                        company={selected}
                        prices={instrumentMarketPrices}
                      />

                      <Card
                        variant={
                          {
                            [CompanyTabs.PERFORMANCE]: CARD_VARIANT[portfolio],
                            [CompanyTabs.ABOUT_COMPANY]:
                              'indigo' as CardVariant,
                          }[activeTab]
                        }
                        padding={{ xs: 'sm', md: 'md', lg: 'lg' }}
                      >
                        <Box
                          height={{
                            md: CARD_BOX_HEIGHT_MD,
                            lg: CARD_BOX_HEIGHT_LG,
                          }}
                        >
                          <Stack spaceY={{ xs: 'sm', md: 'md', lg: 'lg' }}>
                            <TabSwitcher
                              value={activeTab}
                              onChange={(value) => setActiveTab(value)}
                              options={[
                                {
                                  label: 'Performance',
                                  icon: FeatherActivityIcon,
                                  value: CompanyTabs.PERFORMANCE,
                                },
                                {
                                  label: 'About company',
                                  icon: FeatherInfoIcon,
                                  value: CompanyTabs.ABOUT_COMPANY,
                                },
                              ]}
                            />

                            <Box
                              height={{
                                xs: 300,
                                md: `calc(${
                                  CARD_BOX_HEIGHT_MD - TAB_SWITCHER_HEIGHT
                                }px - ${getSpace('md')})`,
                                lg: `calc(${
                                  CARD_BOX_HEIGHT_LG - TAB_SWITCHER_HEIGHT
                                }px - ${getSpace('lg')})`,
                              }}
                            >
                              {
                                {
                                  [CompanyTabs.PERFORMANCE]: (
                                    <CompanyGraph
                                      prices={instrumentMarketPrices}
                                      timeScale={timeScale}
                                      onTimeScaleChange={setTimeScale}
                                      isLoading={!instrumentMarketPrices}
                                    />
                                  ),
                                  [CompanyTabs.ABOUT_COMPANY]: (
                                    <StyledCompanyProfileScrollbarContainer>
                                      <StyledCompanyProfileContainer>
                                        <CompanyProfile
                                          profile={selected?.profile}
                                          portfolio={portfolio}
                                          isLoading={resp.loading}
                                        />
                                      </StyledCompanyProfileContainer>
                                    </StyledCompanyProfileScrollbarContainer>
                                  ),
                                }[activeTab]
                              }
                            </Box>
                          </Stack>
                        </Box>
                      </Card>
                    </Stack>
                  </Stack>
                </TrackingCategory>
              </Columns.Column>
            </Columns>
            <Text
              variant={3}
              color="neutral.080"
              align={{ xs: 'left', md: 'center' }}
            >
              {disclaimer}
            </Text>
          </Stack>
        </Container>
      </Box>
    );
  }),
);

CompaniesInPortfolioPanel.displayName = 'CompaniesInPortfolioPanel';

const useInstrumentMarketPrices = (
  instrumentId?: string,
): Array<DatedBalance> => {
  const resp = useQuery<
    WebUseInstrumentMarketPrices,
    WebUseInstrumentMarketPricesVariables
  >(
    gql`
      query WebUseInstrumentMarketPrices($instrumentId: ID!) {
        instrument(id: $instrumentId) {
          id
          marketPrices {
            date
            audAmount
          }
        }
      }
    `,
    {
      skip: !instrumentId,
      variables: {
        instrumentId: instrumentId ?? '',
      },
    },
  );
  return resp.data?.instrument?.marketPrices ?? [];
};

const StyledCompanyListModal = styled.div<{ isModal: boolean }>`
  display: grid;
  grid-row-gap: ${getSpace('sm')};
  grid-template-rows: auto 1fr;
  ${match('md')`
    grid-template-rows: 1fr auto;
  `}

  ${({ isModal }) =>
    isModal &&
    css`
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      ${zIndex('max')}
      ${backgroundColor('neutral.000')}
    ${paddingX('sm')}
    ${paddingY('lg')}
    `}

  /**
    Positioned absolutely to limit the height of the list to the height
    of the graph+content which is usually shorter in height
  */
  ${match('md')`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    ${padding('none')}
    height: 100%;
  `}
`;

const StyledCompanyProfileScrollbarContainer = styled.div`
  ${scrollbar({
    thumbColor: 'neutral.050',
    trackColor: 'neutral.080',
  })}
  height: 100%;
  margin-right: -${SCROLLBAR_OFFSET};
  overflow: auto;
`;

const StyledCompanyProfileContainer = styled.div`
  padding-right: ${SCROLLBAR_OFFSET};
`;
